/**
 * src/state/conflictReport.js
 *
 * Automatic capture for the "Workspace updated elsewhere" banner.
 *
 * WHY THIS EXISTS
 *
 * That banner tells the user their workspace "was changed in another session"
 * and that their unsaved changes were not saved. When a second person really is
 * editing, that is true and useful. When nobody else is editing, it is the
 * product telling a customer someone else changed their records — a false
 * statement about their own data, and one they cannot act on.
 *
 * The founder reports seeing it repeatedly while working alone, and until now
 * nothing recorded it:
 *
 *   • azureClient.js reports 409s to errorReporter, but deliberately SKIPS the
 *     two reasons in EXPECTED_CONFLICT_REASONS — `stale-version` among them —
 *     on the #5484 rationale that a genuine two-user conflict is the user's own
 *     business rather than a defect. Sound for two users. It is exactly wrong
 *     for one, and one is the case being reported.
 *   • usePersistence logs rich diagnostics at each banner site, but logDiag was
 *     a no-op unless the tenant had opted into diagnostics beforehand — which
 *     no tenant hitting a defect for the first time has done.
 *
 * So the banner fired, and nothing anywhere recorded that it had.
 *
 * WHAT THIS REPORTS, AND WHY EACH FIELD
 *
 * The question a maintainer must answer from the issue alone is: **was there
 * actually a second writer?** Everything here is chosen to answer it.
 *
 *   cause               which of the three banner sites fired. They mean
 *                       different things: a deferral ceiling is the resolution
 *                       never settling, a reverted key is the user's value
 *                       actually being dropped.
 *   overlapShape        `engine-only` means the "other session" was this
 *                       product's own derivation pass. That single value turns
 *                       "someone else changed your records" into a self-
 *                       conflict, which is the most likely explanation for a
 *                       lone user and the hardest to see from a screenshot.
 *   keys                TOP-LEVEL STATE KEY NAMES ONLY — never values, never
 *                       record contents. `risks`, `documents`, `tasks`.
 *   deferrals / runMs   how long the resolution had been failing to settle.
 *   trail               the last diagnostic events, already PII-scrubbed by
 *                       diagnosticsClient at log time.
 *
 * NO TENANT DATA LEAVES THROUGH HERE. Key names, counts, durations and enum
 * strings only. conflictReport.test.js asserts that a record's contents cannot
 * reach the payload, and that assertion is not to be relaxed.
 */

import { reportError } from '../utils/errorReporter.js';
import { recentDiag } from '../diagnostics/diagnosticsClient.js';

/** Trail entries attached to a report. Enough for a save cycle, short enough to read. */
const TRAIL_EVENTS = 25;

/**
 * How the two sides overlapped, as one enum the dedup key can carry.
 *
 * This is the field that decides whether an issue is worth a person's time, so
 * it is derived rather than passed: a caller that forgot to compute it would
 * silently collapse every conflict into one bucket.
 */
function overlapShape({ overlapDetail, unclassifiable, revertedKeys }) {
  if (unclassifiable) return 'unclassifiable';
  if (everyClassifiedKeyIs(overlapDetail, 'engine-only')) return 'engine-only';
  if (Array.isArray(revertedKeys) && revertedKeys.length) return 'reverted';
  return 'record-overlap';
}

/**
 * True when at least one key was classified and EVERY classified key carries
 * `verdict`.
 *
 * usePersistence builds overlapDetail as a per-key map — `{ documents:
 * 'confirmed', tasks: 'engine-only' }` — never as a bare string. The first
 * version of overlapShape compared it against the string `'engine-only'`, so
 * the branch could not be reached from the only caller that exists: every
 * engine-only self-conflict was filed under `reverted` or `record-overlap`,
 * and the one value that says "the other writer was this product's own
 * derivation pass" never appeared on an issue or in a dedup key.
 *
 * A bare string is still accepted, because the field is read from a caller's
 * fact bag rather than a typed contract and a future site may pass one.
 */
function everyClassifiedKeyIs(overlapDetail, verdict) {
  if (typeof overlapDetail === 'string') return overlapDetail === verdict;
  if (!overlapDetail || typeof overlapDetail !== 'object') return false;
  const verdicts = Object.values(overlapDetail);
  return verdicts.length > 0 && verdicts.every((v) => v === verdict);
}

/**
 * The per-key verdicts, as key names mapped to the classifier's own enum.
 *
 * This is the field that answers the question the banner itself cannot: a
 * `confirmed` verdict means the record really was changed on both sides, while
 * `unknown` means the classifier could not tell and reverted the user's edit
 * conservatively. Those are opposite diagnoses — a genuine second writer
 * versus a change nobody else touched being dropped — and until now neither
 * reached the issue, so every report had to be read as "maybe either".
 *
 * Key names and enum strings only, capped the same way keyNames is. A verdict
 * outside the known set is recorded as `unknown` rather than passed through —
 * the values here come from a caller's fact bag, and the no-tenant-data
 * guarantee must not depend on that caller having built it correctly.
 */
const KNOWN_VERDICTS = new Set(['confirmed', 'engine-only', 'different-records', 'unknown']);

function detailByKey(overlapDetail) {
  if (!overlapDetail || typeof overlapDetail !== 'object') return null;
  const out = {};
  let n = 0;
  for (const [k, v] of Object.entries(overlapDetail)) {
    if (typeof k !== 'string' || !k) continue;
    out[k.slice(0, 64)] = KNOWN_VERDICTS.has(v) ? v : 'unknown';
    if (++n >= 20) break;
  }
  return n ? out : null;
}

/** Key names only, capped, and never anything that could be a value. */
function keyNames(...lists) {
  const out = new Set();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const k of list) {
      if (typeof k === 'string' && k) out.add(k.slice(0, 64));
      if (out.size >= 20) break;
    }
  }
  return [...out].sort();
}

/**
 * Report that the conflict banner was raised.
 *
 * Silent and fire-and-forget: errorReporter batches, dedups per page session,
 * and folds a later occurrence onto the issue that already tracks it. Never
 * throws into the save path — a reporting failure must not become a save
 * failure.
 *
 * @param {string} cause — the banner site: 'deferral-ceiling' |
 *   'merge-retry-ceiling' | 'reverted-or-unclassifiable'
 * @param {object} facts — the same object the adjacent logDiag call is given
 */
export function reportConflictBanner(cause, facts = {}) {
  try {
    const shape = overlapShape(facts);
    const keys = keyNames(facts.overlappingKeys, facts.revertedKeys, facts.userChangedKeys);

    reportError({
      message:
        `"Workspace updated elsewhere" was shown to the user (${cause}, ${shape})` +
        (keys.length ? ` on ${keys.join(', ')}` : ''),
      context: `state-conflict:${cause}:${shape}`,
      // One issue per (cause, overlap shape). The message is otherwise
      // identical for every occurrence, and there is no meaningful stack — this
      // is raised from a resolution path, not thrown. Splitting on the shape is
      // the point: `engine-only` is a different defect from `record-overlap`
      // and must not be folded onto it.
      dedupKey: `state-conflict|${cause}|${shape}`,
      stack: JSON.stringify(
        {
          cause,
          overlapShape: shape,
          keys,
          overlapDetail: detailByKey(facts.overlapDetail),
          source: typeof facts.source === 'string' ? facts.source : null,
          deferrals: Number.isFinite(facts.deferrals) ? facts.deferrals : null,
          runMs: Number.isFinite(facts.runMs) ? facts.runMs : null,
          staleMsAtConflict: Number.isFinite(facts.staleMsAtConflict)
            ? facts.staleMsAtConflict
            : null,
          unclassifiable: Boolean(facts.unclassifiable),
          trail: recentDiag(TRAIL_EVENTS),
        },
        null,
        2
      ),
    });
  } catch {
    /* Reporting is never allowed to break a save. */
  }
}
